import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { poolAutomationClient } from '../api';
import { usePoolDispatch, usePoolEligibility, usePoolSave } from '../hooks';
import {
  excludedSummary,
  pausedText,
  poolStatusText,
  waveTriggerText,
} from '../poolStatus';

import { InlineWarning } from './AutomationSwitch';

import Avatar from '@/routes/Agency/DashboardV2/components/Avatar';
import { formatMarkedAt } from '@/routes/Agency/DashboardV2/util/dateFormat';

import '../../DashboardV2/dashboardV2.css';

/*
 * GRAVY-10898 — "Pool auto-invite" for one job (PD-1.x, PD-1.10, G5), opened
 * from the Dashboard V2 Shift Detail panel and the Job page. No prototype
 * exists; it is the Invite backups modal's shell, rows and yellow confirm.
 *
 * It answers the three questions ops ask about an automatically invited job:
 * where the ladder stands (pool -> marketplace -> 2 hour alert), what each
 * wave sent and who it left out, and — from the eligibility read — which pool
 * members could be invited now and WHY each other member was left out (too
 * far, conflict, missing the skill, ...). That last list is the point of the
 * screen: "why wasn't my preferred worker invited?".
 *
 * Pause stops further waves and the widening to the marketplace until
 * resumed; the 2 hour ops alert still fires and Take Over is not affected.
 * It changes what GravyWork does by itself, so it asks first. Resume is
 * immediate: it carries on from the current step and sends a wave now. The
 * server refuses a pause (422, `errors[0]`) for a job the ladder never works
 * on — client not opted in, a long-term assignment, already started, posted
 * before opt-in — and that message is shown as is.
 */

type Props = {
  jobId: number | string;
  /** The shift's timezone, for the wave and pause times. */
  timezone?: string | null;
  /** Pause / Resume are TENANT_ADMIN-role only on the server. */
  canManage?: boolean;
  onClose: () => void;
};

const distanceLabel = (mi?: number | null) =>
  mi == null ? null : `${mi.toFixed(1)} mi`;

const tierLabel = (tier?: string | null) =>
  tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : null;

const PoolAutoInviteModal = ({
  jobId,
  timezone,
  canManage = true,
  onClose,
}: Props) => {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<Element | null>(
    typeof document === 'undefined' ? null : document.activeElement,
  );
  const dispatch = usePoolDispatch(jobId);
  const eligibility = usePoolEligibility(jobId);
  const [confirmingPause, setConfirmingPause] = useState(false);

  const control = usePoolSave(
    (action: 'pause' | 'resume') =>
      action === 'pause'
        ? poolAutomationClient.pause(jobId)
        : poolAutomationClient.resume(jobId),
    'Could not change pool auto-invite for this job. Please try again.',
  );

  useEffect(() => {
    closeRef.current?.focus();
    const opener = openerRef.current as HTMLElement | null;
    return () => opener?.focus?.();
  }, []);

  // Capture phase and stopPropagation: the Shift Detail panel underneath also
  // closes on Escape. An open pause warning handles Escape itself, first (on
  // window), so one Escape never closes the warning AND the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  // reason -> the server's words, for the waves' excluded counts.
  const labels = useMemo(() => {
    const out: Record<string, string> = {};
    eligibility.data?.excluded.forEach((x) => {
      if (x.reason_label) out[x.reason] = x.reason_label;
    });
    return out;
  }, [eligibility.data]);

  const d = dispatch.data;
  const paused = d ? pausedText(d, timezone) : null;

  const confirmPause = async () => {
    setConfirmingPause(false);
    await control.save('pause');
  };

  const actions =
    d && canManage ? (
      d.paused ? (
        <button
          type="button"
          className="dashv2-panel-btn is-secondary"
          data-testid="pool-auto-invite-resume"
          disabled={control.saving}
          onClick={() => void control.save('resume')}
        >
          {control.saving ? 'Resuming…' : 'Resume'}
        </button>
      ) : (
        <button
          type="button"
          className="dashv2-panel-btn is-secondary"
          data-testid="pool-auto-invite-pause"
          disabled={control.saving || confirmingPause}
          onClick={() => setConfirmingPause(true)}
        >
          {control.saving ? 'Pausing…' : 'Pause'}
        </button>
      )
    ) : null;

  const eligibilitySections = eligibility.isLoading ? (
    <p className="dashv2-actmodal-state">Loading pool members…</p>
  ) : eligibility.isError || !eligibility.data ? (
    <p
      className="dashv2-actmodal-state"
      role="alert"
      data-testid="pool-eligibility-error"
    >
      Unable to load the pool for this job.
    </p>
  ) : (
    <>
      {eligibility.data.job_located === false && (
        <p className="dashv2-backup-blocked" data-testid="pool-no-location">
          This job has no location, so pool members can’t be matched by
          distance.
        </p>
      )}
      <section className="dashv2-backup-section">
        <h3 className="dashv2-backup-sectiontitle">
          Can be invited ({eligibility.data.eligible.length})
        </h3>
        {eligibility.data.eligible.length === 0 ? (
          <p className="dashv2-panel-empty" data-testid="pool-eligible-empty">
            {eligibility.data.members_count === 0
              ? 'This client has no preferred pool for this skill.'
              : 'Nobody in the pool can be invited right now.'}
          </p>
        ) : (
          // In invite order, as the server ranked them.
          eligibility.data.eligible.map((m) => {
            const meta = [
              m.rank != null ? `#${m.rank}` : null,
              tierLabel(m.tier),
              distanceLabel(m.distance_mi),
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <div
                key={m.worker_id}
                className="dashv2-backup-row"
                data-testid="pool-eligible-row"
                data-worker-id={m.worker_id}
              >
                <Avatar name={m.name} />
                <span className="dashv2-backup-namecol">
                  <span className="dashv2-backup-name">
                    {m.name || 'Unknown worker'}
                  </span>
                  {meta && <span className="dashv2-backup-meta">{meta}</span>}
                </span>
              </div>
            );
          })
        )}
      </section>
      <section className="dashv2-backup-section">
        <h3 className="dashv2-backup-sectiontitle">
          Left out ({eligibility.data.excluded.length})
        </h3>
        {eligibility.data.excluded.length === 0 ? (
          <p className="dashv2-panel-empty" data-testid="pool-excluded-empty">
            No pool member was left out.
          </p>
        ) : (
          eligibility.data.excluded.map((m) => {
            const meta = [
              m.reason_label ?? m.reason.replace(/_/g, ' '),
              distanceLabel(m.distance_mi),
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <div
                key={m.worker_id}
                className="dashv2-backup-row"
                data-testid="pool-excluded-row"
                data-worker-id={m.worker_id}
                data-reason={m.reason}
              >
                <Avatar name={m.name} />
                <span className="dashv2-backup-namecol">
                  <span className="dashv2-backup-name">
                    {m.name || 'Unknown worker'}
                  </span>
                  <span className="dashv2-backup-meta">{meta}</span>
                </span>
              </div>
            );
          })
        )}
      </section>
    </>
  );

  return (
    <div
      className="dashv2-scope dashv2-actmodal-overlay dashv2-backupmodal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="dashv2-actmodal dashv2-backupmodal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="pool-auto-invite-modal"
      >
        <div className="dashv2-actmodal-head">
          <h2 id={titleId} className="dashv2-actmodal-title">
            Pool auto-invite
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="dashv2-panel-close"
            aria-label="Close pool auto-invite"
            onClick={onClose}
          >
            <XMarkIcon />
          </button>
        </div>

        <div className="dashv2-actmodal-body dashv2-backupmodal-body">
          {dispatch.isLoading && !d ? (
            <p className="dashv2-actmodal-state">Loading…</p>
          ) : dispatch.isError || !d ? (
            <p
              className="dashv2-actmodal-state"
              role="alert"
              data-testid="pool-auto-invite-load-error"
            >
              Unable to load pool auto-invite for this job.
            </p>
          ) : (
            <>
              <div className="dashv2-poolstatus dashv2-backupmodal-setting">
                <span>
                  <span data-testid="pool-auto-invite-status">
                    {poolStatusText(d)}
                  </span>
                  {paused && (
                    <>
                      {' · '}
                      <span data-testid="pool-auto-invite-paused">
                        {paused}
                      </span>
                    </>
                  )}
                </span>
                {actions}
              </div>

              {confirmingPause && (
                <InlineWarning
                  title="Pause pool auto-invite for this job?"
                  goLabel="Pause"
                  onCancel={() => setConfirmingPause(false)}
                  onConfirm={confirmPause}
                  testId="pool-auto-invite-pause-confirm"
                >
                  <ul>
                    <li>
                      No more pool waves, and the job is not opened to the
                      marketplace, until it is resumed.
                    </li>
                    <li>Invites already sent stay.</li>
                    <li>
                      Ops are still alerted at 2 hours if it is unfilled. Take
                      Over is not affected.
                    </li>
                    <li>Resuming carries on from where it stopped.</li>
                  </ul>
                </InlineWarning>
              )}

              {control.error && (
                <p
                  className="dashv2-bkpset-error"
                  role="alert"
                  data-testid="pool-auto-invite-error"
                >
                  {control.error}
                </p>
              )}

              <section className="dashv2-backup-section">
                <h3 className="dashv2-backup-sectiontitle">
                  Waves ({d.waves.length})
                </h3>
                {d.waves.length === 0 ? (
                  <p
                    className="dashv2-panel-empty"
                    data-testid="pool-auto-invite-waves-empty"
                  >
                    No pool invites have been sent for this job.
                  </p>
                ) : (
                  d.waves.map((w) => {
                    const leftOut = excludedSummary(w.excluded_counts, labels);
                    const meta = [
                      waveTriggerText(w.trigger),
                      formatMarkedAt(w.sent_at, timezone),
                      leftOut ? `Left out: ${leftOut}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ');
                    return (
                      <div
                        key={w.wave_number}
                        className="dashv2-backup-row"
                        data-testid="pool-auto-invite-wave"
                        data-wave={w.wave_number}
                      >
                        <span className="dashv2-backup-namecol">
                          <span className="dashv2-backup-name">
                            Wave {w.wave_number} · {w.invited_count} invited
                          </span>
                          {meta && (
                            <span className="dashv2-backup-meta">{meta}</span>
                          )}
                        </span>
                      </div>
                    );
                  })
                )}
              </section>

              {eligibilitySections}
            </>
          )}
        </div>

        <div className="dashv2-actmodal-foot dashv2-backupmodal-foot">
          <button
            type="button"
            className="dashv2-panel-btn is-secondary"
            data-testid="pool-auto-invite-close"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PoolAutoInviteModal;
